/**
 * PlayerCoverWithAds — wraps the player cover artwork and rotates with an
 * admin-managed advert every 30 s while audio is playing.
 *
 * Props:
 *   coverSrc       — the cover image URL (or null to show the fallback)
 *   coverFallback  — JSX rendered when coverSrc is missing/broken
 *   onCoverError   — called when the <img> fires onError
 *   isPlaying      — only rotate while audio is actually playing so a
 *                    paused listener isn't bombarded with ads
 *   title          — used as <img alt>
 *
 * The advert is fetched once on mount, then a new one is fetched on every
 * rotation tick. Impressions + clicks are tracked server-side via
 * `/api/player-ads/{id}/track`. If no ads exist the cover stays put.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROTATE_INTERVAL_MS = 30_000;   // 30 s
const COVER_DWELL_MS = 20_000;       // cover holds 20 s of every 30 s
const AD_DWELL_MS = 10_000;          // ad shows for 10 s

const PlayerCoverWithAds = ({ coverSrc, coverFallback, onCoverError, isPlaying, title }) => {
  const [ad, setAd] = useState(null);
  const [showingAd, setShowingAd] = useState(false);
  const [adImgFailed, setAdImgFailed] = useState(false);
  const rotateTimerRef = useRef(null);
  const impressionFiredRef = useRef(new Set());

  // Pull a fresh weighted-random ad from the server.
  const refreshAd = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/player-ads/serve`);
      if (!r.ok) return;
      const data = await r.json();
      setAd(data.ad || null);
      setAdImgFailed(false);
    } catch { /* silent — ads are best-effort */ }
  }, []);

  // Fire impression once per unique ad shown.
  useEffect(() => {
    if (!ad || !showingAd) return;
    if (impressionFiredRef.current.has(ad.ad_id)) return;
    impressionFiredRef.current.add(ad.ad_id);
    fetch(`${BACKEND_URL}/api/player-ads/${ad.ad_id}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'impression' }),
    }).catch(() => {});
  }, [ad, showingAd]);

  // Initial fetch.
  useEffect(() => { refreshAd(); }, [refreshAd]);

  // Drive the cover↔ad rotation only while playing.
  useEffect(() => {
    if (!isPlaying || !ad) {
      setShowingAd(false);
      if (rotateTimerRef.current) clearTimeout(rotateTimerRef.current);
      return;
    }
    let cancelled = false;
    const cycle = () => {
      if (cancelled) return;
      setShowingAd(true);
      rotateTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        setShowingAd(false);
        // Pre-fetch the next ad while the cover is showing so the next
        // ad-flip is instant.
        refreshAd();
        rotateTimerRef.current = setTimeout(cycle, COVER_DWELL_MS);
      }, AD_DWELL_MS);
    };
    // First cover dwell.
    rotateTimerRef.current = setTimeout(cycle, COVER_DWELL_MS);
    return () => {
      cancelled = true;
      if (rotateTimerRef.current) clearTimeout(rotateTimerRef.current);
    };
  }, [isPlaying, ad, refreshAd]);

  const handleAdClick = () => {
    if (!ad?.click_url) return;
    fetch(`${BACKEND_URL}/api/player-ads/${ad.ad_id}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'click' }),
    }).catch(() => {});
    window.open(ad.click_url, '_blank', 'noopener,noreferrer');
  };

  const showAd = showingAd && ad && !adImgFailed;

  return (
    <>
      {showAd ? (
        <button
          type="button"
          onClick={handleAdClick}
          className="absolute inset-0 group w-full h-full focus:outline-none"
          data-testid="player-ad-slot"
          aria-label={`Advert — ${ad.label}`}
        >
          <img
            src={ad.image_url}
            alt={ad.label}
            className="w-full h-full object-cover transition-opacity duration-500"
            onError={() => setAdImgFailed(true)}
            data-testid="player-ad-img"
          />
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[9px] uppercase tracking-widest font-bold text-amber-200 border border-amber-400/30">
            Ad{ad.sponsor_name ? ` · ${ad.sponsor_name}` : ''}
          </div>
          {ad.click_url && (
            <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-indigo-500/80 text-[9px] uppercase tracking-widest font-bold text-white">
              Tap →
            </div>
          )}
        </button>
      ) : coverSrc ? (
        <img
          src={coverSrc}
          alt={title || ''}
          className="w-full h-full object-cover transition-opacity duration-500"
          onError={onCoverError}
          data-testid="player-cover-img"
        />
      ) : (
        coverFallback
      )}
    </>
  );
};

export default PlayerCoverWithAds;
